export const MAX_ATTENDANCE_RANGE_DAYS = 31;

function entryTimestamp(emp) {
  const raw = emp?.clock_in_raw || emp?.clock_in_actual;
  if (!raw) return Number.POSITIVE_INFINITY;
  const t = new Date(raw).getTime();
  return Number.isNaN(t) ? Number.POSITIVE_INFINITY : t;
}

/** Orden: fecha, hora de entrada registrada y, a igualdad, nombre. */
export function compareAttendanceRows(a, b) {
  const dateCmp = (a.row_date || '').localeCompare(b.row_date || '');
  if (dateCmp !== 0) return dateCmp;
  const entryCmp = entryTimestamp(a) - entryTimestamp(b);
  if (entryCmp !== 0) return entryCmp;
  return (a.employee_name || '').localeCompare(b.employee_name || '', 'es');
}

/**
 * @param {string} isoOrDate
 * @returns {string} YYYY-MM-DD
 */
export function normalizeAttendanceDate(isoOrDate) {
  if (!isoOrDate) return '';
  const s = String(isoOrDate);
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  const day = s.match(/^(\d{4}-\d{2}-\d{2})/);
  if (day) return day[1];
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s.slice(0, 10);
  return d.toLocaleDateString('en-CA', { timeZone: 'America/Santiago' });
}

/**
 * @param {string} from YYYY-MM-DD
 * @param {string} to YYYY-MM-DD
 * @returns {string[]}
 */
export function eachDayInRange(from, to) {
  const start = parseYmd(from);
  const end = parseYmd(to);
  if (!start || !end || end < start) {
    return [];
  }
  const days = [];
  const cursor = new Date(start.getTime());
  while (cursor <= end) {
    days.push(formatYmd(cursor));
    cursor.setUTCDate(cursor.getUTCDate() + 1);
  }
  return days;
}

function parseYmd(value) {
  const m = String(value || '').match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return null;
  return new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
}

function formatYmd(d) {
  const y = d.getUTCFullYear();
  const mo = String(d.getUTCMonth() + 1).padStart(2, '0');
  const day = String(d.getUTCDate()).padStart(2, '0');
  return `${y}-${mo}-${day}`;
}

/**
 * @param {string} from
 * @param {string} to
 * @returns {{ valid: boolean, error?: string, dayCount?: number }}
 */
export function validateAttendanceDateRange(from, to) {
  if (!from || !to) {
    return { valid: false, error: 'Indica fecha de inicio y fin.' };
  }
  const days = eachDayInRange(from, to);
  if (days.length === 0) {
    return { valid: false, error: 'La fecha fin debe ser igual o posterior a la de inicio.' };
  }
  if (days.length > MAX_ATTENDANCE_RANGE_DAYS) {
    return {
      valid: false,
      error: `El rango no puede superar ${MAX_ATTENDANCE_RANGE_DAYS} días.`,
      dayCount: days.length,
    };
  }
  return { valid: true, dayCount: days.length };
}

/**
 * @param {object|null|undefined} data Respuesta single-day o range del API
 * @param {boolean} isRange
 * @returns {{ patternName: string, employees: Array, totalInPattern: number }}
 */
export function flattenAttendanceResponse(data, isRange) {
  if (!data) {
    return { patternName: '', employees: [], totalInPattern: 0 };
  }

  if (!isRange) {
    const employees = Array.isArray(data.employees) ? data.employees : [];
    const rowDate = normalizeAttendanceDate(data.date);
    return {
      patternName: data.pattern_name ?? '',
      employees: employees.map((emp) => ({
        ...emp,
        row_date: rowDate,
        pattern_id: emp.pattern_id ?? data.pattern_id,
        pattern_name: emp.pattern_name ?? data.pattern_name ?? '',
      })),
      totalInPattern: employees.length,
    };
  }

  const days = Array.isArray(data.days) ? data.days : [];
  const rows = [];
  let totalInPattern = 0;

  for (const day of days) {
    const rowDate = normalizeAttendanceDate(day.date);
    const emps = Array.isArray(day.employees) ? day.employees : [];
    totalInPattern += emps.length;
    for (const emp of emps) {
      rows.push({
        ...emp,
        row_date: rowDate,
        pattern_id: emp.pattern_id ?? day.pattern_id ?? data.pattern_id,
        pattern_name: emp.pattern_name ?? day.pattern_name ?? data.pattern_name ?? '',
      });
    }
  }

  rows.sort(compareAttendanceRows);

  return {
    patternName: data.pattern_name ?? '',
    employees: rows,
    totalInPattern,
  };
}

/**
 * Une varias respuestas de asistencia (un patrón por elemento).
 * @param {Array<object>} responses
 * @param {boolean} isRange
 */
export function flattenAttendanceCollection(responses, isRange) {
  const list = Array.isArray(responses) ? responses : [];
  const employees = [];
  let totalInPattern = 0;
  const names = [];

  for (const data of list) {
    const flat = flattenAttendanceResponse(data, isRange);
    if (flat.patternName) names.push(flat.patternName);
    totalInPattern += flat.totalInPattern;
    employees.push(...flat.employees);
  }

  employees.sort(compareAttendanceRows);

  const uniqueNames = [...new Set(names)];
  return {
    patternName: uniqueNames.length === 1 ? uniqueNames[0] : '',
    employees,
    totalInPattern,
  };
}
